import React, { useEffect, useRef, useState } from 'react'
import { Box, Button, TextField, Typography } from '@material-ui/core'
import clsx from 'clsx'

import { useVoiceAgent } from '../../../hooks/useVoiceAgent'
import { VoiceAgentTone } from '../../../models/VoiceAgent'
import { formatE164, TONE_OPTIONS } from '../constants'
import { useVoiceAgentStyles } from '../useVoiceAgentStyles'
import { usePhoneStyles } from '../../Phone/usePhoneStyles'
import { ServiceRequestToggle } from './ServiceRequestToggle'

export const AgentPersonaPanel: React.FC = () => {
    const classes = useVoiceAgentStyles()
    const phone = usePhoneStyles()
    const { setupData, agentConfig, saveAgent } = useVoiceAgent()
    const [agentName, setAgentName] = useState('')
    const [tone, setTone] = useState<VoiceAgentTone>('warm')
    const [transferPhone, setTransferPhone] = useState('')
    const hydrated = useRef(false)

    useEffect(() => {
        if (!setupData || hydrated.current) return
        setAgentName(agentConfig?.agent_name ?? setupData.agent_name ?? 'Alex')
        setTone(agentConfig?.tone ?? setupData.tone ?? 'warm')
        const savedTransfer =
            agentConfig?.human_transfer_e164 ??
            setupData.human_transfer_e164 ??
            ''
        setTransferPhone(savedTransfer ? formatE164(savedTransfer) : '')
        hydrated.current = true
    }, [setupData, agentConfig])

    const handleSave = async () => {
        try {
            await saveAgent.mutateAsync({
                agent_name: agentName,
                tone,
                human_transfer_e164: transferPhone,
            })
        } catch {
            // toast handled by hook
        }
    }

    return (
        <Box className={phone.card} p={3} style={{ maxWidth: 720 }}>
            <Typography className={classes.fieldSectionTitle}>
                Agent voice & persona
            </Typography>
            <Typography className={classes.fieldSectionDesc}>
                Choose how the agent introduces itself and the tone it uses on
                the line.
            </Typography>
            <Box className={classes.fieldGroup}>
                <TextField
                    label="Name"
                    value={agentName}
                    onChange={(e) => setAgentName(e.target.value.slice(0, 50))}
                    fullWidth
                    variant="outlined"
                    size="small"
                />
            </Box>
            <Box className={classes.toneGrid}>
                {TONE_OPTIONS.map((opt) => (
                    <Box
                        key={opt.value}
                        className={clsx(
                            classes.toneCard,
                            tone === opt.value && classes.toneCardSelected,
                        )}
                        role="button"
                        tabIndex={0}
                        onClick={() => setTone(opt.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault()
                                setTone(opt.value)
                            }
                        }}
                    >
                        <Typography className={classes.toneName}>
                            {opt.label}
                        </Typography>
                        <Typography className={classes.toneDesc}>
                            {opt.description}
                        </Typography>
                    </Box>
                ))}
            </Box>
            <ServiceRequestToggle />
            <Box className={classes.fieldGroup} style={{ marginTop: 24 }}>
                <Typography className={classes.fieldSectionTitle}>
                    Human transfer number
                </Typography>
                <Typography
                    className={classes.fieldSectionDesc}
                    style={{ marginBottom: 12 }}
                >
                    If a caller asks for a person twice, the agent transfers
                    them to this number. Leave blank to take a callback instead.
                    Do not use a number that forwards back to the voice agent.
                </Typography>
                <TextField
                    label="Transfer phone number"
                    placeholder="(512) 555-0199"
                    value={transferPhone}
                    onChange={(e) => setTransferPhone(e.target.value)}
                    fullWidth
                    variant="outlined"
                    size="small"
                    inputProps={{ maxLength: 40 }}
                />
            </Box>
            <Button
                variant="contained"
                color="primary"
                className={classes.btnPrimary}
                onClick={handleSave}
                disabled={saveAgent.isLoading}
            >
                {saveAgent.isLoading ? 'Saving...' : 'Save agent'}
            </Button>
        </Box>
    )
}
